import 'bootstrap/dist/css/bootstrap.css';
import 'bootstrap-select/dist/css/bootstrap-select.css';

import '@fortawesome/fontawesome-free/css/fontawesome.css';
import '@fortawesome/fontawesome-free/css/solid.css';

import 'datatables.net-dt/css/dataTables.dataTables.css';
import 'datatables.net-buttons-dt/css/buttons.dataTables.css';
import 'datatables.net-responsive-dt/css/responsive.dataTables.css';
import 'datatables.net-select-dt/css/select.dataTables.css';

import '../css/loading.css';
import '../css/portal.css';

/* Expose jQuery and its alias $ into the global scope. Some libraries like Bootstrap and datatables
 * install jQuery extension functions, but they do so only if the global $ object exists. */
import $ from 'expose-loader?exposes=$,jQuery!jquery';
import 'jquery-validation';
import 'jquery-validation/dist/additional-methods';

/* Expose bootstrap into the global scope (bootstrap-select depends on this) */
import * as bootstrap from 'expose-loader?exposes=bootstrap!bootstrap';
window.bootstrap = bootstrap;
import 'bootstrap-select';

// Patch: reset internal data before each rebuild so refresh() is idempotent
if ($.fn.selectpicker && $.fn.selectpicker.Constructor) {
  const _orig = $.fn.selectpicker.Constructor.prototype.buildData;
  $.fn.selectpicker.Constructor.prototype.buildData = function (data, type) {
    if (!type || type === 'data') this.selectpicker.main.data = [];
    return _orig.call(this, data, type);
  };
}

import 'datatables.net-dt';
import 'datatables.net-buttons/js/buttons.colVis';
import 'datatables.net-buttons/js/buttons.html5';
import 'datatables.net-buttons-dt';
import 'datatables.net-responsive-dt';
import 'datatables.net-select-dt';

import { getAuthToken, isTokenExpiringSoon, logoutFromIdp, refreshAccessToken, storeUserData } from './js/portal/auth';
import { CONF } from './js/portal/environment';
import { baseApiAxios } from './js/portal/api';
import { showErrorFromApiOperation, updateTableKeyBinding } from './js/portal/main';
import { configureSidebar, startNavigationProcess } from './js/portal/sidebar';
import { configureTempPrivilegeTopNav } from './js/portal/temporary_privilege_escalation';

import '../js/load_component';
import './js/portal/datatables_custom_plugins';

import App from './app';

import changelogHtml from './_changelog.html';
import { load_micro_service_changelogs } from './js/portal/micro_changelogs';
import { Modal } from 'bootstrap';

import { get_feature_flag_property } from './js/portal/feature_flags';
import { redirectLegacyUrlIfEnabled } from './js/portal/legacy_redirects';

// Feature flag names used by the sidebar and top-nav. Pre-fetching these
// warms the sessionStorage cache so that configureSidebar/configureTempPrivilegeTopNav
// resolve near-instantly instead of making 8+ sequential API round-trips.
const SIDEBAR_FEATURE_FLAGS = [
  'idc',
  'classic-ad-user-mgmt',
  'network',
  'fqdn',
  '4wheels',
  'grant-purchase-role',
  'gaia-chatbot',
  'scp-changelog',
  'temporary-privilege-escalation',
];

function prefetchFeatureFlags() {
  // Fire all flag lookups in parallel. Each call is Mutex-guarded per flag name,
  // so concurrent calls for the same flag are deduplicated automatically.
  // We only need one property per flag to warm the cache for that flag name.
  return Promise.allSettled(SIDEBAR_FEATURE_FLAGS.map(flag => get_feature_flag_property(`${flag}.enabled`)));
}

const startApp = async app => {
  $('body').append(app);

  // Proactively refresh the access token if it's expired or close to expiry
  // (within 5 minutes). This avoids a needless 403 round-trip on the first
  // API call after a page reload (e.g. triggered by a microfrontend that
  // encountered a 401/403). If the token is still fresh we skip the refresh
  // entirely to avoid a wasteful network call on every page load.
  if (isTokenExpiringSoon()) {
    const refreshed = await refreshAccessToken().catch(() => false);
    if (!refreshed) {
      // Refresh failed (most likely the refresh token is also expired).
      // Logout immediately instead of letting API calls fire and fail.
      console.warn('Token refresh failed (refresh token likely expired). Redirecting to login.');
      logoutFromIdp();
      return; // stop app initialization
    }
  }

  // Forward legacy portal (manage.*) URLs to the new portal (feature-flag controlled)
  if (await redirectLegacyUrlIfEnabled()) return;

  // Fire non-blocking calls immediately — these don't depend on permissions or sidebar DOM
  prefetchFeatureFlags(); // warm feature flag cache in background
  baseApiAxios // retrieve open search config (non-blocking)
    .getSettingsByKey(['search_service_specifications'])
    .then(settings => {
      const specs = settings.search_service_specifications;
      const keys = [
        ['order_search_enable', specs.order_search_enable],
        ['account_search_enable', specs.account_search_enable],
        ['vpc_search_enable', specs.vpc_search_enable || false],
        ['dns_search_enable', specs.dns_search_enable || false],
        ['four_wheels_search_enable', specs.four_wheels_search_enable || false],
      ];
      keys.forEach(([key, value]) => {
        sessionStorage.setItem(key, value);
        localStorage.setItem(key, value);
      });
    })
    .catch(_err => {
      console.debug('failed to request open search config, turn off open search feature');
      sessionStorage.setItem('order_search_enable', false);
      sessionStorage.setItem('account_search_enable', false);
      sessionStorage.setItem('vpc_search_enable', false);
    });

  // If we already have cached permissions (e.g. page refresh), use them
  // immediately so the sidebar and page content can render without waiting
  // for the API call. We still refresh in the background to pick up any
  // permission changes.
  const hasCachedPermissions = !!localStorage.getItem('permissions');

  if (hasCachedPermissions) {
    // We already have permissions — configure sidebar + start navigation immediately.
    // configureSidebar first applies permission-based visibility (sync/instant),
    // then fetches feature flags (async/slow but likely cached from prefetch above).
    Promise.all([
      configureSidebar($('.sidebar-menu').get(0)),
      configureTempPrivilegeTopNav($('#userDropdownItemTempPrivilegeEscalation').get(0)),
    ]);
    // Reveal sidebar immediately — permission-based items are already shown/hidden
    $('.sidebar').removeClass('sidebar-loading');

    // Refresh permissions in the background (non-blocking).
    // If permissions changed, reconfigure the sidebar to reflect the update.
    const cachedPermissions = localStorage.getItem('permissions');
    baseApiAxios
      .getAccessibleAccounts()
      .then(accessible_accounts => {
        localStorage.setItem('accessible_accounts', accessible_accounts?.accessible_accounts_ids);
        storeUserData(accessible_accounts);

        // If permissions changed, reconfigure sidebar
        if (localStorage.getItem('permissions') !== cachedPermissions) {
          configureSidebar($('.sidebar-menu').get(0));
        }
      })
      .catch(_err => {
        showErrorFromApiOperation('Error while requesting accessible accounts:')(_err);
      });
  } else {
    // First login — no cached data, must wait for the API.
    // Feature flags are already being prefetched in parallel (fired above),
    // so by the time getAccessibleAccounts resolves and configureSidebar runs,
    // the flag cache is likely already warm → configureSidebar resolves near-instantly.
    try {
      const accessible_accounts = await baseApiAxios.getAccessibleAccounts();
      localStorage.setItem('accessible_accounts', accessible_accounts?.accessible_accounts_ids);
      storeUserData(accessible_accounts);
    } catch (_err) {
      showErrorFromApiOperation('Error while requesting accessible accounts:')(_err);
    }

    // Configure sidebar and temp privilege nav AFTER permissions are available.
    // Must await here because some top-level sidebar items (IAM Center, Internet
    // Egress) are controlled by feature flags — showing them prematurely would
    // cause a visible flash. Thanks to the prefetch above, feature flag results
    // are already cached in sessionStorage so this resolves near-instantly.
    //
    // Meanwhile, kick off page content loading in parallel — startNavigationProcess
    // doesn't depend on feature flags (permission checks use build-time config),
    // and the sidebar is still hidden (sidebar-loading) so active-item highlighting
    // is invisible until we reveal it.
    const sidebarReady = Promise.all([
      configureSidebar($('.sidebar-menu').get(0)),
      configureTempPrivilegeTopNav($('#userDropdownItemTempPrivilegeEscalation').get(0)),
    ]).then(() => {
      $('.sidebar').removeClass('sidebar-loading');
    });

    // Begin page content load NOW — don't wait for sidebar feature flags
    startNavigationProcess(sessionStorage.lastHash || '#dashboard');

    // But DO wait for sidebar to finish before proceeding to event binding
    await sidebarReady;
  }

  // Track whether we already started navigation in the first-login path above
  const navigationAlreadyStarted = !hasCachedPermissions;

  // Set the current window hash and search parameters directly after the page is loaded. This, together with the
  // function sidebar.js/startNavigationProcess and sidebar.js/setSearchParamsUrl makes it possible to have deep
  // links, even if there are already search parameters in the url.
  // if (window.location.hash) sessionStorage.lastHash = window.location.hash;
  if (window.location.search) sessionStorage.lastSearch = window.location.search;

  // Forces to reload the page after clicking the same sidebar item twice, for example #itsm is currently loaded
  // and you click ITSM Event Bridge in the sidebar once more will reload the page again. Otherwise it would just
  // ignore the click as the hash value in the URL has not changed.
  $('.sidebar').on('click', '.sidebar-item:not(.sidebar-menu) > a', function () {
    if ($(this).attr('href') === window.location.hash) {
      $(window).trigger('hashchange');
    }
  });

  if (window.location.hash || window.location.pathname === '/' || window.location.pathname === '/aws/index.html') {
    if (!sessionStorage.lastHash || window.location.pathname === '/aws/index.html') {
      if (window.location.hash) {
        sessionStorage.lastHash = window.location.hash;
      } else {
        sessionStorage.lastHash = '#dashboard';
      }
    }

    // Start navigation process — skip if already started in the first-login path
    if (!navigationAlreadyStarted) {
      startNavigationProcess(sessionStorage.lastHash);
    }

    // Check the session validity for every hashchange in the URL
    $(window).on('hashchange', () => {
      startNavigationProcess(window.location.hash);
    });
  } else {
    const pathnames = window.location.pathname.split('/');
    startNavigationProcess(`#${pathnames[1]}`);

    // checkout session is still valid
    // since micro-frontend will bypass most of the auth functions
    // we need an extra check for micro-frontend
    getAuthToken().catch(() => {
      console.debug('No valid session found for micro-frontend');
    });
  }

  // Note: process.env.PORTAL_VERSION gets replaced at build time with the actual version from package.json
  $('#portal-version').text(process.env.PORTAL_VERSION);
  $('#version-bottom').text(CONF.backendEnv);

  // Check if the session is still valid after the authentication information in the localStorage
  // have changed. If the session has expired the user can reload the page or will be forwarded to the login
  // page to authenticate again.
  $(window).on('storage', async storageEvent => {
    if (storageEvent.originalEvent.key && storageEvent.originalEvent.key.includes('alpha_')) {
      getAuthToken().catch(() => {
        console.debug('Auth token no longer valid after storage change');
      });
    }
  });

  // Used for table navigation (left, right, pos1) and to show the portal config (strg+i)
  $(window).on('keydown', function (e) {
    updateTableKeyBinding(e);
  });

  $('#changelog').html(changelogHtml);

  // load micro service changelogs from urls
  load_micro_service_changelogs();

  if ($('#sharedModal').length) {
    const shared_modal = new Modal('#sharedModal'); // NOSONAR
    new Modal('#temporaryRoleModal'); // NOSONAR

    if (shared_modal) {
      $(shared_modal._element).on('hidden.bs.modal', function () {
        shared_modal._dialog.removeAttribute('data-type');
        const modal_footer = shared_modal._dialog.querySelector('.modal-footer');
        if (modal_footer)
          modal_footer.innerHTML =
            '<button type="button" class="btn btn-outline-secondary" data-bs-dismiss="modal">Close</button>';
      });
    }
  }

  // show general search section on all pages (including search page)
  const featureFlag = 'search-service.general_search';
  get_feature_flag_property(featureFlag).then(isEnabled => {
    if (isEnabled) {
      const searchSection = document.getElementById('general-search-section');
      searchSection.classList.remove('d-none');

      // Pre-fill the search input with the query parameter if on search page
      if (window.location.pathname.includes('/search/general-search')) {
        const urlParams = new URLSearchParams(window.location.search);
        const query = urlParams.get('query');
        if (query) {
          const searchInput = document.getElementById('general-search-input');
          if (searchInput) {
            searchInput.value = decodeURIComponent(query);
          }
        }
      }
    }
  });
};

const createApp = () => {
  let app = App();

  // App() returns undefined when redirecting (login or code exchange)
  if (!app) {
    return new Promise(() => {}); // Never resolves — page is redirecting
  }

  return Promise.resolve(app);
};

$(() => {
  createApp().then(app => startApp(app));
});
